/**
 * AnimationStateManager computes animation states from an AST.
 *
 * This class takes an AnimationSequence AST and computes the complete
 * AnimationState for each step, handling directive resolution and
 * participant state management.
 */

import type {
  AnimationSequence,
  Step,
  Directive,
  Participant,
} from '../../parser/types';
import type {
  AnimationState,
  ParticipantState,
  ParticipantVisibility,
} from '../types';

/**
 * Manages computation of animation states from an AST.
 */
export class AnimationStateManager {
  private sequence: AnimationSequence;

  constructor(sequence: AnimationSequence) {
    this.sequence = sequence;
  }

  /**
   * Computes animation states for all steps in the sequence.
   *
   * Each state is computed independently (declaratively) rather than
   * cumulatively, allowing for step scrubbing and easier testing.
   *
   * @returns Array of animation states, one per step
   */
  public computeStates(): AnimationState[] {
    return this.sequence.steps.map((step, index) =>
      this.computeStepState(step, index)
    );
  }

  /**
   * Computes the animation state for a single step.
   */
  private computeStepState(step: Step, stepIndex: number): AnimationState {
    // Initialize participants with default state (dimmed)
    const participants = this.initializeParticipants();

    // Auto-focus/dim based on interactions (if any)
    if (step.interactions.length > 0) {
      this.autoFocusInteractingParticipants(step, participants);
    }

    // Apply explicit directives (these override auto-inference)
    this.applyDirectives(step.directives, participants);

    return {
      stepIndex,
      description: step.description,
      participants,
      interactions: step.interactions,
      fragments: step.fragments,
    };
  }

  /**
   * Initializes all participants with default state (dimmed, not activated).
   */
  private initializeParticipants(): Map<string, ParticipantState> {
    const participants = new Map<string, ParticipantState>();

    this.sequence.participants.forEach(
      (participant: Participant, index: number) => {
        participants.set(participant.name, {
          name: participant.name,
          visibility: 'dimmed',
          position: index,
          activated: false,
        });
      }
    );

    return participants;
  }

  /**
   * Applies directives to participant states.
   *
   * Directives are processed in order, so later directives override earlier
   * ones if they conflict (e.g., focus then dim results in dimmed).
   */
  private applyDirectives(
    directives: Directive[],
    participants: Map<string, ParticipantState>
  ): void {
    for (const directive of directives) {
      // Handle activation directives separately
      if (directive.type === 'activate') {
        for (const participantName of directive.participants) {
          const participant = participants.get(participantName);
          if (participant) {
            participant.activated = true;
          }
        }
      } else if (directive.type === 'deactivate') {
        for (const participantName of directive.participants) {
          const participant = participants.get(participantName);
          if (participant) {
            participant.activated = false;
          }
        }
      } else {
        // Handle visibility directives
        const visibility = this.directiveToVisibility(directive.type);

        for (const participantName of directive.participants) {
          const participant = participants.get(participantName);
          if (participant) {
            participant.visibility = visibility;
          }
        }
      }
    }
  }

  /**
   * Maps directive type to participant visibility.
   */
  private directiveToVisibility(directiveType: string): ParticipantVisibility {
    switch (directiveType) {
      case 'focus':
        return 'focused';
      case 'dim':
        return 'dimmed';
      case 'show':
        return 'normal';
      case 'hide':
        return 'hidden';
      default:
        return 'dimmed';
    }
  }

  /**
   * Auto-focuses participants involved in interactions and dims others.
   *
   * This is a convenience feature: if a step has interactions but no
   * directives, automatically focus the interacting participants and
   * dim all other participants.
   */
  private autoFocusInteractingParticipants(
    step: Step,
    participants: Map<string, ParticipantState>
  ): void {
    const interactingParticipants = new Set<string>();

    for (const interaction of step.interactions) {
      interactingParticipants.add(interaction.from);
      interactingParticipants.add(interaction.to);
    }

    // Focus participants involved in interactions, dim others
    for (const [participantName, participant] of participants) {
      if (interactingParticipants.has(participantName)) {
        participant.visibility = 'focused';
      } else {
        participant.visibility = 'dimmed';
      }
    }
  }
}
